import { useState } from 'react';
import { mappingColumns } from '../companyContact/hook';
import {
  EnvironmentTwoTone,
  EyeOutlined,
  DownloadOutlined,
} from '@ant-design/icons';
import { Button, Typography } from 'antd';
import * as api from 'config/axios';

import { CERTIFICATE_COLUMNS, CERTIFICATE_COLUMNS_AG } from './constants';
import { formatMDY } from 'utils/formatDate';

import { CustomNotification } from 'common/components';
import { sleep } from 'utils/delay';

export const useCertificate = ({ propsData }) => {
  const [certificateList, setCertificateList] = useState(() => {
    return (propsData?.id ? [{ ...propsData }] : [])?.map((product) => {
      return { ...product, key: product?.id };
    });
  });

  const handleDeleteDataSource = (key) => {
    const filter = certificateList.filter((item) => item?.key !== key);
    setCertificateList(filter);
  };

  const addData = (contact) => {
    setCertificateList((pre) => {
      const key = pre[pre?.length - 1]?.key ? pre[pre?.length - 1]?.key + 1 : 1;

      return [...pre, { ...contact, key }];
    });
  };

  const editData = (currentContact) => {
    setCertificateList((pre) => {
      return pre?.map((item) => {
        return item?.key === currentContact?.key
          ? {
            ...currentContact,
            id: propsData?.id ? item?.key : null,
          }
          : item;
      });
    });
  };

  return {
    certificateList,
    addData,
    editData,
    handleDeleteDataSource,
  };
};

export const RenderAssociatedLocations = (props) => {
  const { data, setVisibleAssociateLocal } = props || {};

  return (
    <>
      {data?.associateLocationIds?.length > 0 ? (
        <EnvironmentTwoTone
          style={{ fontSize: 16, cursor: 'pointer' }}
          onClick={async () => {
            await sleep(100);
            setVisibleAssociateLocal(true);
          }}
        />
      ) : (
        ''
      )}
    </>
  );
};

export const useGetCertificateColumnsDef = ({
  setStateActions,
  setVisibleAssociateLocal,
}) => {
  const columnDefs = [
    {
      field: '',
      checkboxSelection: true,
      filter: false,
      suppressMenu: true,
      width: 50,
      resizable: true,
    },
    ...CERTIFICATE_COLUMNS_AG,
    {
      dataType: 'string',
      headerName: 'Certificate Expiry Date',
      fieldName: 'expirationDate',
      fieldNameCamelCase: 'expirationDate',
      resizable: true,
      width: 300,
      valueGetter: ({ data }) => formatMDY(data?.['expirationDate']),
    },
    {
      dataType: 'string',
      headerName: 'Associated Locations',
      fieldName: 'associateLocationIds',
      fieldNameCamelCase: 'associateLocationIds',
      allowFilter: true,
      allowSort: true,
      width: 180,
      cellRenderer: ({ data }) => (
        <RenderAssociatedLocations
          data={data}
          setVisibleAssociateLocal={setVisibleAssociateLocal}
        />
      ),
      resizable: true,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      resizable: true,
      cellRenderer: ({ data }) => (
        <CellRenderAction data={data} setStateActions={setStateActions} />
      ),
    },
  ];

  return { columnDefs };
};

export const CellRenderAction = ({ data, setStateActions }) => {
  return (
    <>
      <Button
        type='link'
        icon={<EyeOutlined />}
        onClick={() => {
          setStateActions({ visible: true, data: data });
        }}
      />
      <Button
        type='link'
        icon={<DownloadOutlined />}
        onClick={() => {
          const downloadUrl = data?.url;

          if (!downloadUrl) {
            CustomNotification.error('Could not download file!');
          }
          api.sendDownload({
            url: downloadUrl,
          });
        }}
      />
    </>
  );
};

export const CellRenderActionClaims = ({ data, setStateActions }) => {
  return (
    <>
      <Button
        type='link'
        icon={<EyeOutlined />}
        onClick={() => {
          if (!data?.certificateUrl) {
            CustomNotification.error('Could not preview: missing file URL.');
            return;
          }
          setStateActions((s) => ({ ...s, visible: true, filedata: data }));
        }}
      />
      <Button
        type='link'
        icon={<DownloadOutlined />}
        onClick={() => {
          const downloadUrl = data.certificateUrl;

          if (!downloadUrl) {
            CustomNotification.error('Could not download file!');
          }
          api.sendDownload({
            url: downloadUrl,
          });
        }}
      />
    </>
  );
};

export const useCertificateColumns = (hiddenFields = [], setStateActions) => {
  const columns = mappingColumns(CERTIFICATE_COLUMNS);

  const customColumns = [
    ...columns,
    {
      title: <Typography.Text strong>Certificate Expiry Date</Typography.Text>,
      dataIndex: 'expirationDate',
      render: (value, _) => {
        return (
          <Typography.Text ellipsis={{ tooltip: formatMDY(value) }}>
            {formatMDY(value)}
          </Typography.Text>
        );
      },
    },
    {
      title: <Typography.Text strong>Actions</Typography.Text>,
      dataIndex: 'actions',
      render: (_, record) => {
        return (
          <CellRenderAction data={record} setStateActions={setStateActions} />
        );
      },
    },
  ];

  const filter = customColumns?.filter(
    (item) => !hiddenFields.includes(item?.dataIndex)
  );

  return {
    columns: filter,
  };
};
